import { FC } from 'react';
import {
  Root,
  Trigger,
  Portal,
  Value,
  Group,
  Label,
  Content,
  Viewport,
  Item,
  ItemText,
  ItemIndicator,
} from '@radix-ui/react-select';
import styled from 'styled-components';
import tw from 'twin.macro';

import { IconSize, Icons, Weights } from '../enums';
import Icon from './Icon';

const StyledTrigger = styled(Trigger)<{
  $disabled?: boolean;
  $hasValue?: boolean;
}>`
  ${tw`inline-flex text-xs items-center justify-center min-w-16 h-8 border rounded-full p-0.5 [&>span]:truncate [&>span]:w-full leading-none gap-[5px] transition shadow-[0_0_0_0_rgb(var(--color-primaryValues)_/_0)] outline-none`}

  ${({ $hasValue }) =>
    $hasValue
      ? tw`bg-primary text-slate-100 border-slate-400`
      : tw`bg-white text-slate-500 border-slate-200`}

  &:hover {
    ${({ $hasValue }) =>
      $hasValue ? tw`text-slate-200` : tw`bg-slate-50 text-slate-800`}
  }

  &:focus {
    ${({ $hasValue }) =>
      $hasValue
        ? tw`shadow-[0_0_0_4px_rgb(var(--color-primaryValue)_/_0.2)] border-primary bg-primary text-slate-50`
        : tw`border-slate-400 bg-white text-slate-500`}
  }

  ${({ $disabled }) => $disabled && tw`cursor-not-allowed opacity-50`}
`;

const StyledContent = styled(Content)`
  ${tw`overflow-hidden bg-white rounded-md z-[20000] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]`};
`;

const StyledViewPort = styled(Viewport)`
  ${tw`p-[5px]`};
`;

const StyledLabel = styled(Label)`
  ${tw`px-1 pb-1 text-xs text-slate-400`};
`;

const StyledItemIndicator = styled(ItemIndicator)`
  ${tw`absolute inline-flex items-center justify-center left-0 w-[25px]`};
`;

const StyledItem = styled(Item)`
  ${tw`relative flex items-center text-sm leading-none text-slate-400 h-[25px] pr-[35px] pl-[25px] z-20 select-none data-[disabled]:text-slate-300 data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[highlighted]:bg-slate-800 data-[highlighted]:text-slate-50 rounded cursor-pointer`};
`;

interface Props {
  label: string;
  ariaLabel: string;
  items: { value: Weights; label: string }[];
  disabled?: boolean;
  value: Weights;
  placeholder?: string;
  className?: string;
  handleValueChange: (value: string) => void;
}

const ButtonSelect: FC<Props> = ({
  value,
  ariaLabel,
  items,
  label,
  placeholder,
  className,
  handleValueChange,
}) => (
  <Root onValueChange={handleValueChange} value={value.toString()}>
    <StyledTrigger
      $hasValue={value.toString() !== '-1'}
      aria-label={ariaLabel}
      className={className}>
      <Value placeholder={placeholder} />
    </StyledTrigger>
    <Portal>
      <StyledContent>
        <StyledViewPort>
          <Group>
            <StyledLabel>{label}</StyledLabel>
            {items.map(({ value, label }) => (
              <StyledItem value={value.toString()} key={value}>
                {/* Prevent google translate crashing */}
                <ItemText><span>{label}</span></ItemText>
                <StyledItemIndicator>
                  <Icon icon={Icons.check} size={IconSize.small} />
                </StyledItemIndicator>
              </StyledItem>
            ))}
          </Group>
        </StyledViewPort>
      </StyledContent>
    </Portal>
  </Root>
);

export default ButtonSelect;
